import React, { useEffect, useState } from "react";
import { api, formatBRL } from "../lib/api";
import { X, Loader2, FileText, Handshake, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

export default function ConvertContractModal({ contract, onClose, onConverted }) {
  const [kind, setKind] = useState("parcelado");
  const [installmentsCount, setInstallmentsCount] = useState(4);
  const [frequency, setFrequency] = useState("weekly");
  const [firstDueDate, setFirstDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [observations, setObservations] = useState("");

  // Defaults vindos do contrato (mostramos como preview, editáveis)
  const capDefault = Number(contract?.capital || 0);
  const intDefault = Number(contract?.current_cycle?.interest_percent || 0);
  const jurosOpenDefault = Number(contract?.current_cycle?.interest_amount || 0);

  const [capital, setCapital] = useState(String(capDefault));
  const [interestPercent, setInterestPercent] = useState(String(intDefault));

  const [agreedAmount, setAgreedAmount] = useState(String((capDefault + jurosOpenDefault).toFixed(2)));
  const [acordoCapitalBase, setAcordoCapitalBase] = useState(String(capDefault));
  const [acordoLucroTotal, setAcordoLucroTotal] = useState(String(jurosOpenDefault));
  const [agreementReason, setAgreementReason] = useState("Conversão de contrato normal");

  const [saving, setSaving] = useState(false);

  // Sincroniza valores do acordo
  useEffect(() => {
    const cap = Number(acordoCapitalBase) || 0;
    const lucro = Number(acordoLucroTotal) || 0;
    setAgreedAmount(String((cap + lucro).toFixed(2)));
  }, [acordoCapitalBase, acordoLucroTotal]);

  // Preview de cálculos
  const capNum = Number(capital) || 0;
  const intNum = Number(interestPercent) || 0;
  const totalParc = capNum * (1 + intNum / 100);
  const parcelaParc = installmentsCount > 0 ? totalParc / installmentsCount : 0;
  const agreedNum = Number(agreedAmount) || 0;
  const parcelaAco = installmentsCount > 0 ? agreedNum / installmentsCount : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        kind,
        installments_count: Number(installmentsCount),
        frequency,
        first_due_date: firstDueDate,
        observations,
      };
      if (kind === "parcelado") {
        body.capital = Number(capital);
        body.interest_percent = Number(interestPercent);
      } else {
        body.acordo_capital_base = Number(acordoCapitalBase);
        body.acordo_lucro_total = Number(acordoLucroTotal);
        body.agreed_amount = Number(agreedAmount);
        body.agreement_reason = agreementReason;
      }
      const { data } = await api.post(`/contracts/${contract.id}/convert`, body);
      toast.success(`Contrato convertido para ${kind}`);
      onConverted && onConverted(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao converter");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="convert-contract-modal">
      <div className="fc-card w-full max-w-2xl my-8 p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1.5">
              <ArrowRightLeft size={11}/> Converter contrato
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">Para Parcelado ou Acordo</h2>
            <div className="text-[12px] text-[#737373] mt-1">
              Contrato original será marcado como "Convertido" e ficará vinculado ao novo registro.
            </div>
          </div>
          <button onClick={onClose} className="fc-btn-ghost" data-testid="close-convert-modal">
            <X size={16}/>
          </button>
        </div>

        {/* Preview do contrato original */}
        <div className="rounded-md border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3 mb-5 text-[12px]">
          <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-1.5">Dados herdados do contrato</div>
          <div className="grid grid-cols-3 gap-2">
            <div><span className="text-[#737373]">Capital:</span> <span className="text-white font-mono">{formatBRL(capDefault)}</span></div>
            <div><span className="text-[#737373]">Juros previsto ciclo:</span> <span className="text-white font-mono">{formatBRL(jurosOpenDefault)}</span></div>
            <div><span className="text-[#737373]">Total devido:</span> <span className="text-[#D4AF37] font-mono">{formatBRL(capDefault + jurosOpenDefault)}</span></div>
          </div>
        </div>

        {/* Tipo */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setKind("parcelado")}
            data-testid="convert-kind-parcelado"
            className={`p-3 rounded-md border text-left transition-colors ${
              kind === "parcelado" ? "border-blue-500/40 bg-blue-500/10" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} className="text-blue-300"/>
              <div className="text-sm font-semibold">Parcelado</div>
            </div>
            <div className="text-[11px] text-[#A3A3A3]">Capital + juros divididos em parcelas iguais</div>
          </button>
          <button
            type="button"
            onClick={() => setKind("acordo")}
            data-testid="convert-kind-acordo"
            className={`p-3 rounded-md border text-left transition-colors ${
              kind === "acordo" ? "border-violet-500/40 bg-violet-500/10" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Handshake size={14} className="text-violet-300"/>
              <div className="text-sm font-semibold">Acordo</div>
            </div>
            <div className="text-[11px] text-[#A3A3A3]">Renegociação (capital + lucro herdados ou ajustáveis)</div>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">Parcelas</span>
              <input type="number" min="1" max="120" className="fc-input mt-1" value={installmentsCount} onChange={(e) => setInstallmentsCount(e.target.value)} required data-testid="convert-installments"/>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">Frequência</span>
              <select className="fc-input mt-1" value={frequency} onChange={(e) => setFrequency(e.target.value)} data-testid="convert-frequency">
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">1ª parcela</span>
              <input type="date" className="fc-input mt-1" value={firstDueDate} onChange={(e) => setFirstDueDate(e.target.value)} required data-testid="convert-first-due"/>
            </label>
          </div>

          {kind === "parcelado" ? (
            <div className="grid grid-cols-2 gap-3 p-4 rounded-md border border-blue-500/20 bg-blue-500/5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-blue-300">Capital (R$)</span>
                <input type="number" step="0.01" min="0.01" className="fc-input mt-1" value={capital} onChange={(e) => setCapital(e.target.value)} required data-testid="convert-capital"/>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-blue-300">Juros (%)</span>
                <input type="number" step="0.01" min="0" className="fc-input mt-1" value={interestPercent} onChange={(e) => setInterestPercent(e.target.value)} required data-testid="convert-interest"/>
              </label>
              {capNum > 0 && installmentsCount > 0 && (
                <div className="col-span-2 text-[12px] text-[#A3A3A3] flex items-center justify-between pt-2 border-t border-blue-500/20">
                  <span>Total: <strong className="text-white">{formatBRL(totalParc)}</strong></span>
                  <span>{installmentsCount}x de <strong className="text-[#D4AF37]">{formatBRL(parcelaParc)}</strong></span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 p-4 rounded-md border border-violet-500/20 bg-violet-500/5">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Capital base (R$)</span>
                  <input type="number" step="0.01" min="0" className="fc-input mt-1" value={acordoCapitalBase} onChange={(e) => setAcordoCapitalBase(e.target.value)} required data-testid="convert-acordo-capital"/>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Lucro/juros (R$)</span>
                  <input type="number" step="0.01" min="0" className="fc-input mt-1" value={acordoLucroTotal} onChange={(e) => setAcordoLucroTotal(e.target.value)} required data-testid="convert-acordo-lucro"/>
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-violet-300">Valor acordado total (R$)</span>
                <input type="number" step="0.01" min="0.01" className="fc-input mt-1" value={agreedAmount} onChange={(e) => setAgreedAmount(e.target.value)} required data-testid="convert-agreed"/>
                <span className="text-[10.5px] text-[#737373] mt-1 block">Capital + Lucro = {formatBRL(Number(acordoCapitalBase) + Number(acordoLucroTotal))}</span>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-violet-300">Motivo do acordo</span>
                <input type="text" className="fc-input mt-1" value={agreementReason} onChange={(e) => setAgreementReason(e.target.value)} data-testid="convert-reason"/>
              </label>
              {agreedNum > 0 && installmentsCount > 0 && (
                <div className="text-[12px] text-[#A3A3A3] flex items-center justify-between pt-2 border-t border-violet-500/20">
                  <span>Total acordado: <strong className="text-white">{formatBRL(agreedNum)}</strong></span>
                  <span>{installmentsCount}x de <strong className="text-[#D4AF37]">{formatBRL(parcelaAco)}</strong></span>
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#737373]">Observações</span>
            <textarea className="fc-input mt-1" rows="2" value={observations} onChange={(e) => setObservations(e.target.value)} data-testid="convert-observations"/>
          </label>

          <div className="text-[11px] text-[#737373] bg-[#0a0a0a] border border-[#1f1f1f] rounded p-2.5 leading-relaxed">
            <strong className="text-amber-300">Importante:</strong> o contrato original será marcado como "Convertido" e seu ciclo aberto será fechado.
            Comissão e capital aplicado do sócio NÃO serão duplicados.
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#1f1f1f]">
            <button type="button" onClick={onClose} className="fc-btn-ghost" data-testid="cancel-convert">Cancelar</button>
            <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="submit-convert">
              {saving ? <><Loader2 size={14} className="animate-spin"/> Convertendo…</> : <>Converter para {kind}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
